import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SignatureCanvas from "react-signature-canvas";
import CancelIcon from "@mui/icons-material/Cancel";
import FingerprintCaptureControls from "../../../FingerprintCaptureControls";
import { useFingerprintCapture } from "../../../../hooks/useFingerprintCapture";

interface ConsentFormState {
  patientName: string;
  guardianName: string;
  diagnosis: string;
  treatmentDate: string | null;
  medicines: string;
  remissionPercent: string;
  relapsePercent: string;
  trmPercent: string;
  serialMonths: string;
  serialCourses: string;
  careFor: string;
  kinName: string;
  kinRelation: string;
  signerRelation: string;
  otherRelation: string;
  patientSignature: string;
  patientCnic: string;
  patientSignDate: string | null;
  patientSignTime: string;
  doctorName: string;
  doctorSignature: string;
  doctorCnic: string;
  doctorSignDate: string | null;
  doctorSignTime: string;
}

const sideEffects = [
  ["بال", "بالوں کا گرنا، جو مکمل ہو سکتا ہے، مگر دوبارہ اگ سکتے ہیں۔"],
  ["کان", "سماعت میں کمی، توازن میں کمی۔"],
  ["منہ", "چھالے، ذائقے میں تبدیلی، گلے کی خراش۔"],
  ["پھیپھڑے", "پھیپھڑوں کو نقصان۔"],
  ["دل", "دل کی دھڑکن میں تبدیلی، سانس کی تنگی کے ساتھ دل کی ناکامی۔"],
  ["ہاضمہ", "قبض، چھالے، سینے کی جلن، متلی، قے، دست، جگر کو نقصان، یرقان۔"],
  ["پیشاب", "پیشاب میں جلن، پیشاب میں خون آنا، گردے کو نقصان۔"],
  [
    "نظامِ غدود",
    "بلڈ شوگر میں اضافہ، تولیدی افعال کی کمزوری (بانجھ پن)، حیض کا بند ہونا، مردانہ خصوصیات میں کمی، گرم چمک، چھاتی کا بڑھنا، چہرے کے بالوں میں اضافہ۔",
  ],
  [
    "اعصاب",
    "انگلیوں، پاؤں کی انگلیوں یا پیروں میں جھنجھناہٹ، بے حسی یا کمزوری، جبڑے میں درد، پٹھوں کی کمزوری، چڑچڑاپن، اداسی، الجھن، بینائی کی کمزوری، چکر آنا۔",
  ],
  [
    "ہڈیوں کا گودا",
    "خون کی کمی، بشمول ہیموگلوبن میں کمی جس سے تھکاوٹ یا کمزوری ہوتی ہے اور سرخ خون کے خلیوں کی منتقلی درکار ہو سکتی ہے؛ سفید خون کے خلیوں میں کمی جس سے انفیکشن کا خطرہ بڑھتا ہے؛ اور پلیٹلیٹس میں کمی جس سے خون بہنے کا خطرہ بڑھتا ہے اور پلیٹلیٹس کی منتقلی درکار ہو سکتی ہے۔",
  ],
  [
    "رگیں",
    "انجکشن کی جگہ اور اس سے اوپر رگوں کا سخت ہو جانا۔ اگر دوا رگ سے باہر نکل جائے تو چھالے اور تکلیف۔ شریانی انجکشن کی جگہ پر چھالے بننا۔ ٹانگوں اور/یا پھیپھڑوں میں خون کے لوتھڑے۔",
  ],
  ["ہڈیاں", "ہڈیوں کا پتلا ہونا، کبھی کبھار فریکچر کے ساتھ۔"],
  ["دیگر", "بخار، سردی لگنا، الرجی کا ردِعمل، جلد پر دانے، وزن میں اضافہ، تھکاوٹ۔"],
];

const relationOptions = [
  { value: "parents", label: "والدین" },
  { value: "guardian", label: "سرپرست" },
  { value: "spouse", label: "شوہر / بیوی" },
  { value: "sibling", label: "بالغ بھائی / بہن" },
  { value: "other", label: "خون کا دیگر رشتہ دار" },
];

const nameFieldSx = {
  "& .MuiInputBase-input": {
    fontFamily: "Arial, sans-serif",
    direction: "ltr",
    textAlign: "left",
    fontSize: "14px",
  },
};

const blankSx = { width: 72, mx: 0.5 };

const SignaturePad = ({
  value,
  padRef,
  onEnd,
  onClear,
}: {
  value: string;
  padRef: React.RefObject<SignatureCanvas | null>;
  onEnd: () => void;
  onClear: () => void;
}) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      maxWidth: 320,
      height: 200,
      bgcolor: "#fff",
      border: "1px solid #d0d0d0",
      borderRadius: "8px",
      overflow: "hidden",
    }}
  >
    {value ? (
      <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
    ) : (
      <SignatureCanvas
        ref={padRef as React.LegacyRef<SignatureCanvas>}
        penColor="black"
        backgroundColor="rgba(0,0,0,0)"
        onEnd={onEnd}
        canvasProps={{
          width: 320,
          height: 200,
          style: { width: "100%", height: "100%", touchAction: "none", cursor: "crosshair" },
        }}
      />
    )}
    <IconButton
      size="small"
      aria-label="Clear signature"
      onClick={onClear}
      sx={{ position: "absolute", top: 4, right: 4, bgcolor: "#d32f2f", color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}
    >
      <CancelIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

const ChemotherapyConsent: React.FC<any> = ({ onChange, data, otherTabsData, profile }) => {
  const patientSignatureRef = useRef<SignatureCanvas | null>(null);
  const doctorSignatureRef = useRef<SignatureCanvas | null>(null);
  const patientName = otherTabsData?.patientData?.patientName || otherTabsData?.patientName || "";
  const guardianName = otherTabsData?.patientData?.guardiansName || otherTabsData?.guardiansName || "";
  const admissionDiagnosis = Array.isArray(otherTabsData?.diagnosis)
    ? otherTabsData.diagnosis.filter(Boolean).join("، ")
    : otherTabsData?.finalDiagnosis || otherTabsData?.diagnosis || "";
  const doctorName = otherTabsData?.doctorName || profile?.data?.fullName || "";

  const [form, setForm] = useState<ConsentFormState>({
    patientName,
    guardianName,
    diagnosis: admissionDiagnosis,
    treatmentDate: dayjs().format("YYYY-MM-DD"),
    medicines: "",
    remissionPercent: "",
    relapsePercent: "",
    trmPercent: "",
    serialMonths: "",
    serialCourses: "",
    careFor: "",
    kinName: "",
    kinRelation: "",
    signerRelation: "",
    otherRelation: "",
    patientSignature: "",
    patientCnic: "",
    patientSignDate: dayjs().format("YYYY-MM-DD"),
    patientSignTime: dayjs().format("hh:mm A"),
    doctorName,
    doctorSignature: "",
    doctorCnic: "",
    doctorSignDate: dayjs().format("YYYY-MM-DD"),
    doctorSignTime: dayjs().format("hh:mm A"),
  });

  const update = (patch: Partial<ConsentFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const patientFingerprint = useFingerprintCapture({
    onCaptured: (fingerprintDataUrl) => update({ patientSignature: fingerprintDataUrl }),
  });
  const doctorFingerprint = useFingerprintCapture({
    onCaptured: (fingerprintDataUrl) => update({ doctorSignature: fingerprintDataUrl }),
  });

  useEffect(() => {
    const saved = data && ("medicines" in data || "patientSignature" in data || "signerRelation" in data);
    if (saved) {
      setForm((prev) => ({
        ...prev,
        ...data,
        patientName: data.patientName || patientName,
        guardianName: data.guardianName || guardianName,
        diagnosis: data.diagnosis || admissionDiagnosis,
        doctorName: data.doctorName || doctorName,
        treatmentDate: data.treatmentDate || prev.treatmentDate,
        patientCnic: data.patientCnic || "",
        patientSignDate: data.patientSignDate || prev.patientSignDate,
        patientSignTime: data.patientSignTime || prev.patientSignTime,
        doctorCnic: data.doctorCnic || "",
        doctorSignDate: data.doctorSignDate || prev.doctorSignDate,
        doctorSignTime: data.doctorSignTime || prev.doctorSignTime,
      }));
      patientFingerprint.syncFingerprintFlag(data.patientSignature);
      doctorFingerprint.syncFingerprintFlag(data.doctorSignature);
      return;
    }
    setForm((prev) => ({
      ...prev,
      patientName: prev.patientName || patientName,
      guardianName: prev.guardianName || guardianName,
      diagnosis: prev.diagnosis || admissionDiagnosis,
      doctorName: prev.doctorName || doctorName,
    }));
  }, [data]);

  useEffect(() => {
    onChange?.(form);
  }, [form]);

  const savePad = (padRef: React.RefObject<SignatureCanvas | null>, field: "patientSignature" | "doctorSignature") => {
    if (!padRef.current || padRef.current.isEmpty()) {
      update({ [field]: "" });
      return;
    }
    update({ [field]: padRef.current.toDataURL() });
  };

  const clearPad = (padRef: React.RefObject<SignatureCanvas | null>, field: "patientSignature" | "doctorSignature") => {
    padRef.current?.clear();
    update({ [field]: "" });
    if (field === "patientSignature") patientFingerprint.clearFingerprint();
    else doctorFingerprint.clearFingerprint();
  };

  return (
    <Box
      className="urduNotationFont"
      dir="rtl"
      sx={{
        m: 1,
        p: { xs: 2, md: 3 },
        bgcolor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        color: "#1a1a1a",
      }}
    >
      <Typography className="urduNotationFont" sx={{ fontSize: "22px", fontWeight: 700, textAlign: "center", mb: 2 }}>
        کیموتھراپی کے اجرا کے لیے رضامندی
      </Typography>

      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 1 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ flex: "1 1 280px" }}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
            مریض کا نام:
          </Typography>
          <TextField value={form.patientName} onChange={(e) => update({ patientName: e.target.value })} variant="standard" fullWidth sx={nameFieldSx} />
        </Box>
        <Box display="flex" alignItems="center" gap={1} sx={{ flex: "1 1 240px" }}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
            والد / سرپرست:
          </Typography>
          <TextField value={form.guardianName} onChange={(e) => update({ guardianName: e.target.value })} variant="standard" fullWidth sx={nameFieldSx} />
        </Box>
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ flex: "1 1 280px" }}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
            تشخیص:
          </Typography>
          <TextField value={form.diagnosis} onChange={(e) => update({ diagnosis: e.target.value })} variant="standard" fullWidth sx={nameFieldSx} />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
            علاج کی تاریخ:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={form.treatmentDate ? dayjs(form.treatmentDate) : null}
              format="DD.MM.YYYY"
              onChange={(value) =>
                update({ treatmentDate: value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : null })
              }
              slotProps={{ textField: { variant: "standard", size: "small", sx: { width: 140 } } }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.4 }}>
        میں اپنی بیماری کے علاج کے لیے مندرجہ ذیل کیموتھراپی ادویات لگوانے کا اختیار دیتا / دیتی ہوں:
      </Typography>
      <TextField
        value={form.medicines}
        onChange={(e) => update({ medicines: e.target.value })}
        variant="standard"
        fullWidth
        multiline
        minRows={2}
        sx={{ ...nameFieldSx, mb: 0.5 }}
      />
      <Typography className="urduNotationFont" sx={{ fontSize: "14px", color: "#333", mb: 2 }}>
        (یہ دوائیں منسلکہ پروٹوکول کے مطابق دی جائیں گی۔)
      </Typography>

      <Typography className="urduNotationFont" sx={{ fontSize: "16px", fontWeight: 700, mb: 0.5 }}>
        صحت کے خطرات
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        ڈاکٹر نے میرے ساتھ مندرجہ بالا ادویات، متوقع فوائد، خطرات اور متبادل علاج پر بات کی ہے۔ مجھے بتایا گیا ہے کہ ٹھیک ہونے کے
        <TextField value={form.remissionPercent} onChange={(e) => update({ remissionPercent: e.target.value })} variant="standard" sx={blankSx} inputProps={{ dir: "ltr", style: { textAlign: "center" } }} />
        فیصد امکانات ہیں،
        <TextField value={form.relapsePercent} onChange={(e) => update({ relapsePercent: e.target.value })} variant="standard" sx={blankSx} inputProps={{ dir: "ltr", style: { textAlign: "center" } }} />
        فیصد دوبارہ ہونے کا خطرہ ہے، اور علاج سے متعلق اموات کا
        <TextField value={form.trmPercent} onChange={(e) => update({ trmPercent: e.target.value })} variant="standard" sx={blankSx} inputProps={{ dir: "ltr", style: { textAlign: "center" } }} />
        فیصد خطرہ ہے۔ مجھے علاج کے دوران پیش آنے والے مسائل کے بارے میں بتایا گیا ہے۔ میں جانتا / جانتی ہوں کہ نتائج کی ضمانت نہیں دی جا سکتی۔ یہ اجازت اس سمجھ کے ساتھ دی جاتی ہے کہ ہر علاج میں کچھ خطرات شامل ہوتے ہیں۔ مجھے بتایا گیا ہے کہ اس علاج کے مندرجہ ذیل میں سے کوئی ایک یا تمام ضمنی اثرات ہو سکتے ہیں، جو شدید یا جان لیوا ہو سکتے ہیں۔
      </Typography>

      <Box sx={{ border: "1px solid #ececec", borderRadius: "8px", overflow: "hidden", my: 1.5 }}>
        {sideEffects.map(([title, detail]) => (
          <Box key={title} display="flex" gap={1.5} sx={{ px: 1.5, py: 0.6, borderBottom: "1px solid #f0f0f0", "&:last-child": { borderBottom: "none" } }}>
            <Typography className="urduNotationFont" sx={{ fontSize: "14px", fontWeight: 700, minWidth: 110 }}>
              {title}
            </Typography>
            <Typography className="urduNotationFont" sx={{ fontSize: "14px", lineHeight: 2.2, flex: 1 }}>
              {detail}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5, mt: 1 }}>
        میں سمجھتا / سمجھتی ہوں کہ طریقۂ کار کے دوران تصویر اور/یا ویڈیو یا الیکٹرانک ریکارڈنگ ہو سکتی ہے، اور اسے ہسپتال کی کارکردگی میں بہتری یا تعلیمی مقاصد کے لیے استعمال کیا جا سکتا ہے۔
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        میں یہ بھی سمجھتا / سمجھتی ہوں کہ اگر کوئی شدید جان لیوا پیچیدگی میری موت کا سبب بنے تو موت کی وجہ کی تحقیق کے لیے سوئی سے بایوپسی کی جا سکتی ہے۔
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        میں اس بات سے اتفاق کرتا / کرتی ہوں کہ ضرورت پڑنے پر میرے نمونے تحقیقی مقاصد کے لیے استعمال کیے جا سکتے ہیں۔
      </Typography>

      <Typography className="urduNotationFont" sx={{ fontSize: "16px", fontWeight: 700, mt: 2 }}>
        سلسلہ وار علاج کے لیے رضامندی (جب قابلِ اطلاق ہو)
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        میں مذکورہ بالا علاج کو ایک سلسلے کے طور پر تقریباً
        <TextField value={form.serialMonths} onChange={(e) => update({ serialMonths: e.target.value })} variant="standard" sx={blankSx} inputProps={{ dir: "ltr", style: { textAlign: "center" } }} />
        ماہ کے لیے رضامندی دیتا / دیتی ہوں، جس میں میری بیماری کے مرحلے کے مطابق تقریباً
        <TextField value={form.serialCourses} onChange={(e) => update({ serialCourses: e.target.value })} variant="standard" sx={blankSx} inputProps={{ dir: "ltr", style: { textAlign: "center" } }} />
        کورس شامل ہیں۔
      </Typography>

      <Typography className="urduNotationFont" sx={{ fontSize: "16px", fontWeight: 700, mt: 2 }}>
        مریض یا قانونی نمائندے کا بیان
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        ذیل میں دستخط کر کے میں بتاتا / بتاتی ہوں کہ میری عمر 18 سال یا اس سے زیادہ ہے، یا بصورتِ دیگر رضامندی دینے کا مجاز ہوں۔ میں نے اس فارم کے مندرجات پڑھ لیے ہیں یا مجھے سمجھائے گئے ہیں، اور میں ان سے اتفاق کرتا / کرتی ہوں۔ میں اپنے / اپنی
        <TextField value={form.careFor} onChange={(e) => update({ careFor: e.target.value })} variant="standard" sx={{ width: 140, mx: 0.5, ...nameFieldSx }} />
        کے لیے اس رضامندی میں درج دیکھ بھال، علاج یا خدمات حاصل کرنے کی اجازت دیتا / دیتی ہوں۔ مجھے سوال پوچھنے کا موقع ملا ہے اور میرے تمام سوالوں کے جواب دیے گئے ہیں۔
      </Typography>

      <Typography className="urduNotationFont" sx={{ fontSize: "16px", fontWeight: 700, mt: 2 }}>
        قریبی رشتہ دار
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5 }}>
        میں بذریعۂ ہذا
        <TextField value={form.kinName} onChange={(e) => update({ kinName: e.target.value })} variant="standard" sx={{ width: 180, mx: 0.5, ...nameFieldSx }} />
        ، جو میرا / میری
        <TextField value={form.kinRelation} onChange={(e) => update({ kinRelation: e.target.value })} variant="standard" sx={{ width: 140, mx: 0.5, ...nameFieldSx }} />
        ہے، کو اپنا قریبی رشتہ دار مقرر کرتا / کرتی ہوں۔ بیماری یا علاج کی پیچیدگیوں کی وجہ سے اگر میں ذہنی طور پر فیصلہ کرنے کے قابل نہ رہوں تو وہ میری طرف سے میرے علاج کے بارے میں فیصلے کرنے کا مجاز ہوگا / ہوگی۔
      </Typography>

      <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8f8fb", borderRadius: "8px" }}>
        <Typography className="urduNotationFont" sx={{ fontSize: "14px", mb: 0.5 }}>
          اگر مریض کے سوا کوئی اور دستخط کرے تو مریض سے رشتہ منتخب کریں:
        </Typography>
        <RadioGroup row value={form.signerRelation} onChange={(e) => update({ signerRelation: e.target.value })}>
          {relationOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={<Typography className="urduNotationFont" sx={{ fontSize: "14px" }}>{option.label}</Typography>}
            />
          ))}
        </RadioGroup>
        {form.signerRelation === "other" && (
          <TextField
            value={form.otherRelation}
            onChange={(e) => update({ otherRelation: e.target.value })}
            variant="standard"
            placeholder="وضاحت کریں"
            sx={{ width: 260, ...nameFieldSx }}
          />
        )}
        <Typography className="urduNotationFont" sx={{ fontSize: "13px", color: "#555", mt: 1 }}>
          * ہسپتال کی اخلاقیات کی مشاورتی کمیٹی سے جائزہ اور منظوری درکار ہے۔
        </Typography>
      </Box>

      <Typography className="urduNotationFont" sx={{ fontSize: "16px", fontWeight: 700, mt: 2 }}>
        ڈاکٹر کا بیان
      </Typography>
      <Typography className="urduNotationFont" sx={{ fontSize: "15px", lineHeight: 2.5, mb: 2 }}>
        میں نے اس فارم میں بیان کردہ کیموتھراپی کی وضاحت کر دی ہے، جس میں ممکنہ خطرات، پیچیدگیاں، متبادل علاج (بشمول علاج نہ کروانا) اور متوقع نتائج مریض اور/یا اس کے نمائندے کو شامل ہیں۔ مریض اور/یا ان کے نمائندے نے مجھے بتایا ہے کہ وہ اس فارم کے مندرجات کو سمجھتے ہیں۔
      </Typography>

      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, maxWidth: 420 }}>
        <Typography className="urduNotationFont" sx={{ fontSize: "15px", whiteSpace: "nowrap" }}>
          ڈاکٹر کا نام:
        </Typography>
        <TextField value={form.doctorName} onChange={(e) => update({ doctorName: e.target.value })} variant="standard" fullWidth sx={nameFieldSx} />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <Box sx={{ flex: "1 1 320px", bgcolor: "#f3f4f6", borderRadius: "12px", p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", fontWeight: 700, textAlign: "center" }}>
            مریض / قانونی نمائندے کے دستخط / انگوٹھا
          </Typography>
          <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%" }}>
            <Typography className="urduNotationFont" sx={{ fontSize: "14px", whiteSpace: "nowrap" }}>شناختی کارڈ:</Typography>
            <TextField value={form.patientCnic} onChange={(e) => update({ patientCnic: e.target.value })} variant="standard" fullWidth placeholder="XXXXX-XXXXXXX-X" sx={nameFieldSx} />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className="urduNotationFont" sx={{ fontSize: "14px" }}>تاریخ:</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={form.patientSignDate ? dayjs(form.patientSignDate) : null}
                  format="DD.MM.YYYY"
                  onChange={(value) => update({ patientSignDate: value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : null })}
                  slotProps={{ textField: { variant: "standard", size: "small", sx: { width: 130 } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className="urduNotationFont" sx={{ fontSize: "14px" }}>وقت:</Typography>
              <TextField value={form.patientSignTime} onChange={(e) => update({ patientSignTime: e.target.value })} variant="standard" placeholder="hh:mm A" sx={{ width: 100, ...nameFieldSx }} />
            </Box>
          </Box>
          <FingerprintCaptureControls
            fingerprintDevice={patientFingerprint.fingerprintDevice}
            detectingDevice={patientFingerprint.detectingDevice}
            capturingFingerprint={patientFingerprint.capturingFingerprint}
            onDetect={() => patientFingerprint.detectDevice(true)}
            onCapture={patientFingerprint.captureFingerprint}
          />
          <SignaturePad
            value={form.patientSignature}
            padRef={patientSignatureRef}
            onEnd={() => savePad(patientSignatureRef, "patientSignature")}
            onClear={() => clearPad(patientSignatureRef, "patientSignature")}
          />
        </Box>
        <Box sx={{ flex: "1 1 320px", bgcolor: "#f3f4f6", borderRadius: "12px", p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography className="urduNotationFont" sx={{ fontSize: "15px", fontWeight: 700, textAlign: "center" }}>
            ڈاکٹر کے دستخط / انگوٹھا
          </Typography>
          <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%" }}>
            <Typography className="urduNotationFont" sx={{ fontSize: "14px", whiteSpace: "nowrap" }}>شناختی کارڈ:</Typography>
            <TextField value={form.doctorCnic} onChange={(e) => update({ doctorCnic: e.target.value })} variant="standard" fullWidth placeholder="XXXXX-XXXXXXX-X" sx={nameFieldSx} />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className="urduNotationFont" sx={{ fontSize: "14px" }}>تاریخ:</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={form.doctorSignDate ? dayjs(form.doctorSignDate) : null}
                  format="DD.MM.YYYY"
                  onChange={(value) => update({ doctorSignDate: value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : null })}
                  slotProps={{ textField: { variant: "standard", size: "small", sx: { width: 130 } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className="urduNotationFont" sx={{ fontSize: "14px" }}>وقت:</Typography>
              <TextField value={form.doctorSignTime} onChange={(e) => update({ doctorSignTime: e.target.value })} variant="standard" placeholder="hh:mm A" sx={{ width: 100, ...nameFieldSx }} />
            </Box>
          </Box>
          <FingerprintCaptureControls
            fingerprintDevice={doctorFingerprint.fingerprintDevice}
            detectingDevice={doctorFingerprint.detectingDevice}
            capturingFingerprint={doctorFingerprint.capturingFingerprint}
            onDetect={() => doctorFingerprint.detectDevice(true)}
            onCapture={doctorFingerprint.captureFingerprint}
          />
          <SignaturePad
            value={form.doctorSignature}
            padRef={doctorSignatureRef}
            onEnd={() => savePad(doctorSignatureRef, "doctorSignature")}
            onClear={() => clearPad(doctorSignatureRef, "doctorSignature")}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ChemotherapyConsent;

const printLine = {
  fontFamily: '"Noto Nastaliq Urdu", "NotoNastaliqUrdu", serif',
  fontSize: "11px",
  lineHeight: 2.15,
  textAlign: "right" as const,
};

const filled = (value?: string) => value?.trim() || "________";

export const ChemotherapyConsentPrint = ({ formData }: { formData: any }) => {
  const data = formData || {};
  const relationLabel =
    relationOptions.find(option => option.value === data.signerRelation)?.label || "";

  return (
    <Box dir="rtl" sx={{ color: "#000" }}>
      <Typography sx={{ ...printLine, fontSize: "16px", fontWeight: 700, textAlign: "center", mb: 1 }}>
        کیموتھراپی کے اجرا کے لیے رضامندی
      </Typography>
      <Typography sx={printLine}>
        مریض کا نام: {filled(data.patientName)}
        <Box component="span" sx={{ display: "inline-block", width: 24 }} />
        والد / سرپرست: {filled(data.guardianName)}
      </Typography>
      <Typography sx={printLine}>
        تشخیص: {filled(data.diagnosis)}
        <Box component="span" sx={{ display: "inline-block", width: 24 }} />
        علاج کی تاریخ: {data.treatmentDate ? dayjs(data.treatmentDate).format("DD.MM.YYYY") : "________"}
      </Typography>
      <Typography sx={{ ...printLine, mt: 1 }}>
        میں اپنی بیماری کے علاج کے لیے مندرجہ ذیل کیموتھراپی ادویات لگوانے کا اختیار دیتا / دیتی ہوں:
      </Typography>
      <Typography sx={{ ...printLine, fontFamily: "Arial, sans-serif", textAlign: "left", direction: "ltr" }}>
        {filled(data.medicines)}
      </Typography>
      <Typography sx={printLine}>(یہ دوائیں منسلکہ پروٹوکول کے مطابق دی جائیں گی۔)</Typography>

      <Typography sx={{ ...printLine, fontWeight: 700, mt: 1 }}>صحت کے خطرات</Typography>
      <Typography sx={printLine}>
        ڈاکٹر نے میرے ساتھ مندرجہ بالا ادویات، متوقع فوائد، خطرات اور متبادل علاج پر بات کی ہے۔ مجھے بتایا گیا ہے کہ ٹھیک ہونے کے {filled(data.remissionPercent)} فیصد امکانات ہیں، {filled(data.relapsePercent)} فیصد دوبارہ ہونے کا خطرہ ہے، اور علاج سے متعلق اموات کا {filled(data.trmPercent)} فیصد خطرہ ہے۔ مجھے علاج کے دوران پیش آنے والے مسائل کے بارے میں بتایا گیا ہے۔ میں جانتا / جانتی ہوں کہ نتائج کی ضمانت نہیں دی جا سکتی۔ یہ اجازت اس سمجھ کے ساتھ دی جاتی ہے کہ ہر علاج میں کچھ خطرات شامل ہوتے ہیں۔ مجھے بتایا گیا ہے کہ اس علاج کے مندرجہ ذیل میں سے کوئی ایک یا تمام ضمنی اثرات ہو سکتے ہیں، جو شدید یا جان لیوا ہو سکتے ہیں۔
      </Typography>

      <Box sx={{ border: "1px solid #000", my: 0.5 }}>
        {sideEffects.map(([title, detail]) => (
          <Box key={title} display="flex" gap={1} sx={{ px: 0.8, borderBottom: "1px solid #ccc", "&:last-child": { borderBottom: "none" } }}>
            <Typography sx={{ ...printLine, fontWeight: 700, minWidth: 90 }}>{title}</Typography>
            <Typography sx={{ ...printLine, flex: 1 }}>{detail}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={printLine}>
        میں سمجھتا / سمجھتی ہوں کہ طریقۂ کار کے دوران تصویر اور/یا ویڈیو یا الیکٹرانک ریکارڈنگ ہو سکتی ہے، اور اسے ہسپتال کی کارکردگی میں بہتری یا تعلیمی مقاصد کے لیے استعمال کیا جا سکتا ہے۔
      </Typography>
      <Typography sx={printLine}>
        میں یہ بھی سمجھتا / سمجھتی ہوں کہ اگر کوئی شدید جان لیوا پیچیدگی میری موت کا سبب بنے تو موت کی وجہ کی تحقیق کے لیے سوئی سے بایوپسی کی جا سکتی ہے۔
      </Typography>
      <Typography sx={printLine}>
        میں اس بات سے اتفاق کرتا / کرتی ہوں کہ ضرورت پڑنے پر میرے نمونے تحقیقی مقاصد کے لیے استعمال کیے جا سکتے ہیں۔
      </Typography>
      <Typography sx={{ ...printLine, fontWeight: 700, mt: 0.5 }}>سلسلہ وار علاج کے لیے رضامندی (جب قابلِ اطلاق ہو)</Typography>
      <Typography sx={printLine}>
        میں مذکورہ بالا علاج کو ایک سلسلے کے طور پر تقریباً {filled(data.serialMonths)} ماہ کے لیے رضامندی دیتا / دیتی ہوں، جس میں میری بیماری کے مرحلے کے مطابق تقریباً {filled(data.serialCourses)} کورس شامل ہیں۔
      </Typography>
      <Typography sx={{ ...printLine, fontWeight: 700, mt: 0.5 }}>مریض یا قانونی نمائندے کا بیان</Typography>
      <Typography sx={printLine}>
        ذیل میں دستخط کر کے میں بتاتا / بتاتی ہوں کہ میری عمر 18 سال یا اس سے زیادہ ہے، یا بصورتِ دیگر رضامندی دینے کا مجاز ہوں۔ میں نے اس فارم کے مندرجات پڑھ لیے ہیں یا مجھے سمجھائے گئے ہیں، اور میں ان سے اتفاق کرتا / کرتی ہوں۔ میں اپنے / اپنی {filled(data.careFor)} کے لیے اس رضامندی میں درج دیکھ بھال، علاج یا خدمات حاصل کرنے کی اجازت دیتا / دیتی ہوں۔ مجھے سوال پوچھنے کا موقع ملا ہے اور میرے تمام سوالوں کے جواب دیے گئے ہیں۔
      </Typography>
      <Typography sx={{ ...printLine, fontWeight: 700, mt: 0.5 }}>قریبی رشتہ دار</Typography>
      <Typography sx={printLine}>
        میں بذریعۂ ہذا {filled(data.kinName)}، جو میرا / میری {filled(data.kinRelation)} ہے، کو اپنا قریبی رشتہ دار مقرر کرتا / کرتی ہوں۔ بیماری یا علاج کی پیچیدگیوں کی وجہ سے اگر میں ذہنی طور پر فیصلہ کرنے کے قابل نہ رہوں تو وہ میری طرف سے میرے علاج کے بارے میں فیصلے کرنے کا مجاز ہوگا / ہوگی۔
      </Typography>
      <Typography sx={printLine}>
        اگر مریض کے سوا کوئی اور دستخط کرے تو مریض سے رشتہ: {relationLabel || "________"}
        {data.signerRelation === "other" ? ` (${filled(data.otherRelation)})` : ""}
      </Typography>
      <Typography sx={{ ...printLine, fontSize: "10px" }}>
        * ہسپتال کی اخلاقیات کی مشاورتی کمیٹی سے جائزہ اور منظوری درکار ہے۔
      </Typography>
      <Typography sx={{ ...printLine, fontWeight: 700, mt: 0.5 }}>ڈاکٹر کا بیان</Typography>
      <Typography sx={printLine}>
        میں نے اس فارم میں بیان کردہ کیموتھراپی کی وضاحت کر دی ہے، جس میں ممکنہ خطرات، پیچیدگیاں، متبادل علاج (بشمول علاج نہ کروانا) اور متوقع نتائج مریض اور/یا اس کے نمائندے کو شامل ہیں۔ مریض اور/یا ان کے نمائندے نے مجھے بتایا ہے کہ وہ اس فارم کے مندرجات کو سمجھتے ہیں۔
      </Typography>
      <Typography sx={printLine}>ڈاکٹر کا نام: {filled(data.doctorName)}</Typography>

      <Box display="flex" gap={2} sx={{ mt: 1 }}>
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ ...printLine, fontWeight: 700 }}>مریض / قانونی نمائندے کے دستخط / انگوٹھا</Typography>
          <Typography sx={printLine}>شناختی کارڈ: {filled(data.patientCnic)}</Typography>
          <Typography sx={printLine}>
            تاریخ: {data.patientSignDate ? dayjs(data.patientSignDate).format("DD.MM.YYYY") : "________"}
            <Box component="span" sx={{ display: "inline-block", width: 16 }} />
            وقت: {filled(data.patientSignTime)}
          </Typography>
          {data.patientSignature ? (
            <Box component="img" src={data.patientSignature} alt="Patient signature" sx={{ width: 160, height: 90, objectFit: "contain", border: "1px solid #000" }} />
          ) : (
            <Typography sx={printLine}>________________</Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ ...printLine, fontWeight: 700 }}>ڈاکٹر کے دستخط / انگوٹھا</Typography>
          <Typography sx={printLine}>شناختی کارڈ: {filled(data.doctorCnic)}</Typography>
          <Typography sx={printLine}>
            تاریخ: {data.doctorSignDate ? dayjs(data.doctorSignDate).format("DD.MM.YYYY") : "________"}
            <Box component="span" sx={{ display: "inline-block", width: 16 }} />
            وقت: {filled(data.doctorSignTime)}
          </Typography>
          {data.doctorSignature ? (
            <Box component="img" src={data.doctorSignature} alt="Doctor signature" sx={{ width: 160, height: 90, objectFit: "contain", border: "1px solid #000" }} />
          ) : (
            <Typography sx={printLine}>________________</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

